import { motion } from "framer-motion";
import { Check, Moon, ArrowDown, Plus } from "lucide-react";
import { MENUS } from "@/lib/data";

const EASE = [0.22, 1, 0.36, 1];

export default function MenuSection({ selectedMenu, onSelectMenu }) {
    return (
        <section id="menuer" data-testid="menu-section" className="grain relative py-28 sm:py-36">
            <div className="mx-auto max-w-6xl px-5">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mb-16 max-w-xl"
                >
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-poelse">01 — Menuer</p>
                    <h2 className="font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                        Fire menuer. Vælg din vej.
                    </h2>
                    <p className="mt-4 text-base text-muted-foreground sm:text-lg">
                        Klik på en menu for at vælge den — så ruller vi direkte videre til
                        konfiguratoren. Priser viser vi ikke her — du får et personligt tilbud, når
                        Jørgen ringer dig op.
                    </p>
                </motion.div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {MENUS.map((m, i) => {
                        const active = selectedMenu === m.id;
                        return (
                            <motion.button
                                key={m.id}
                                type="button"
                                data-testid={`menu-card-${m.id}`}
                                onClick={() => onSelectMenu(m.id)}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                                className={`group relative flex cursor-pointer flex-col overflow-hidden bg-paper p-4 pb-6 text-left shadow-[0_18px_50px_rgba(11,42,51,0.08)] ring-1 ring-border transition-[transform,box-shadow,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(15,76,92,0.16)] ${
                                    active ? "ring-2 ring-poelse" : ""
                                } ${i % 2 === 1 ? "lg:translate-y-6" : ""}`}
                            >
                                <div className="relative overflow-hidden rounded-t-[999px]">
                                    <img
                                        src={m.image}
                                        alt={`${m.name} — ${m.tagline}`}
                                        loading="lazy"
                                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    {active && (
                                        <span className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full bg-poelse text-paper">
                                            <Check className="h-4 w-4" />
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-1 flex-col px-2 pt-5">
                                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-poelse">{m.name}</p>
                                    <h3 className="mt-1.5 font-serif text-xl font-semibold text-ink sm:text-2xl">{m.tagline}</h3>
                                    <div className="my-4 border-t border-dashed border-poelse/20" />
                                    <ul className="flex-1 space-y-2.5">
                                        {m.items.map((item) => {
                                            const isExtra = (m.extras || []).includes(item);
                                            return (
                                                <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                                                    {isExtra ? (
                                                        <Plus className="mt-0.5 h-4 w-4 shrink-0 text-poelse" />
                                                    ) : (
                                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-poelse" />
                                                    )}
                                                    <span className={isExtra ? "font-semibold text-ink" : ""}>{item}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <p
                                        className={`mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                                            active ? "text-poelse" : "text-muted-foreground group-hover:text-poelse"
                                        }`}
                                    >
                                        {active ? (
                                            <>
                                                <Check className="h-3.5 w-3.5" /> Valgt — videre herunder
                                            </>
                                        ) : (
                                            <>
                                                Klik for at vælge <ArrowDown className="h-3.5 w-3.5" />
                                            </>
                                        )}
                                    </p>
                                </div>
                                {(m.id === "menu1" || m.id === "menu2") && (
                                    <div
                                        data-testid={`menu-card-${m.id}-natmad-badge`}
                                        className="-mx-4 -mb-6 mt-4 bg-poelse px-3 py-1.5 text-center font-mono text-xs font-bold uppercase tracking-[0.15em] text-paper"
                                    >
                                        Kan fås som natmad
                                    </div>
                                )}
                            </motion.button>
                        );
                    })}
                </div>
                <motion.div
                    data-testid="natmad-banner"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mt-16 flex flex-col items-start gap-5 rounded-2xl border-2 border-dashed border-poelse bg-sky-soft p-7 sm:flex-row sm:items-center"
                >
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-poelse text-paper">
                        <Moon className="h-5 w-5" />
                    </span>
                    <div>
                        <h3 className="font-serif text-xl font-semibold text-ink">Natmad bestilles som selvstændigt arrangement.</h3>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                            Forkæl gæsterne med lidt lækkert.
                            <br />
                            Før det er tid til at gå hjem
                            <br />
                            eller til de seje som fester videre i de sene nattetimer.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
